import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../seo/SEO';

/**
 * Mentions légales — version "particulier-artisan" sans inscription
 * commerciale (Auto-Entrepreneur ou SARL). Conforme à la loi marocaine
 * 53-05 sur l'échange électronique de données juridiques pour un site
 * d'information non-commercial.
 *
 * Notes pour le mode "sans AE" :
 *  - Pas de mention d'IF / ICE / numéro AE (n'existent pas encore)
 *  - L'éditrice est identifiée comme personne physique, propriétaire d'un
 *    atelier artisanal individuel
 *  - Aucune transaction de vente à distance ne s'effectue via le site :
 *    les pièces se réservent ensuite par échange direct (WhatsApp, RDV
 *    physique). Cette précision déplace le site hors du champ strict de
 *    la loi 31-08 sur la vente à distance.
 *  - Récépissé CNDP : à compléter une fois la déclaration soumise sur
 *    cndp.ma (gratuite, indépendante du statut commercial)
 *
 * À adapter le jour où le statut commercial change.
 */
export function MentionsLegalesPage() {
  const { t } = useTranslation();

  return (
    <section className="max-w-editorial mx-auto px-6 sm:px-10 py-20">
      <SEO titleKey="seo.legal.title" descriptionKey="seo.legal.description" />
      <div className="grid grid-cols-12 gap-8 mb-16 border-b border-linen pb-12">
        <div className="col-span-12 md:col-span-3">
          <span className="editorial-number text-5xl block">N°LL</span>
          <div className="editorial-label mt-3">— Légal</div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h1 className="font-display text-[clamp(2.5rem,5vw,4.5rem)] leading-[0.95]">
            Mentions légales
          </h1>
          <p className="mt-6 editorial-label">Dernière mise à jour : mai 2026</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-9 md:col-start-4 space-y-12 font-body text-base text-ink-soft leading-relaxed">

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">1. Éditrice du site</h2>
            <p>
              Le présent site <strong>faticaftan.com</strong> est édité, à titre individuel et
              artisanal, par&nbsp;:
            </p>
            <ul className="mt-3 space-y-1 list-disc list-inside">
              <li><strong>Nom d'usage&nbsp;:</strong> Fati Caftan</li>
              <li><strong>Identité de l'éditrice&nbsp;:</strong> Fatima [Nom de famille à compléter]</li>
              <li><strong>Statut&nbsp;:</strong> Particulière — atelier artisanal de couture traditionnelle marocaine</li>
              <li><strong>Adresse de correspondance&nbsp;:</strong> [À compléter — ville, Maroc]</li>
              <li><strong>Email&nbsp;:</strong> <a href="mailto:fatima@faticaftan.com" className="text-brass hover:text-brass-dark underline">fatima@faticaftan.com</a></li>
              <li><strong>Téléphone / WhatsApp&nbsp;:</strong> +212 7 01 04 86 61</li>
            </ul>
            <p className="mt-3 italic text-sm">
              Aucune transaction commerciale n'est conclue directement sur ce site. Les pièces
              du catalogue se réservent par contact direct (WhatsApp, rendez-vous en atelier),
              et la vente définitive intervient en présentiel après devis ou essayage. Le site
              joue le rôle d'une vitrine éditoriale et d'un canal de prise de rendez-vous.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">2. Directrice de la publication</h2>
            <p>
              Fatima [Nom de famille à compléter], éditrice et seule responsable du contenu
              publié sur faticaftan.com.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">3. Hébergement</h2>
            <p>Le site est hébergé sur un serveur opéré par&nbsp;:</p>
            <ul className="mt-3 space-y-1 list-disc list-inside">
              <li><strong>Hébergeur&nbsp;:</strong> Hetzner Online GmbH</li>
              <li><strong>Adresse&nbsp;:</strong> Industriestr. 25, 91710 Gunzenhausen, Allemagne</li>
              <li><strong>Téléphone&nbsp;:</strong> +49 (0)9831 505-0</li>
              <li>
                <strong>Site web&nbsp;:</strong>{' '}
                <a
                  href="https://www.hetzner.com"
                  className="text-brass hover:text-brass-dark underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.hetzner.com
                </a>
              </li>
              <li><strong>Localisation du serveur&nbsp;:</strong> Helsinki, Finlande (Union européenne)</li>
            </ul>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">4. Nom de domaine</h2>
            <p>
              Les noms de domaine <strong>faticaftan.com</strong> et son alias{' '}
              <strong>fati-caftan.com</strong> sont enregistrés auprès du registrar{' '}
              <a
                href="https://porkbun.com"
                className="text-brass hover:text-brass-dark underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Porkbun
              </a>{' '}
              (Porkbun LLC, États-Unis).
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">5. Protection des données personnelles</h2>
            <p>
              Le traitement des données personnelles collectées via ce site est effectué dans le
              respect de la loi marocaine n°09-08 relative à la protection des personnes physiques
              à l'égard du traitement des données à caractère personnel, et — pour les visiteuses
              résidentes UE — du Règlement Général sur la Protection des Données (RGPD).
            </p>
            <p className="mt-3">
              Pour le détail des données collectées, leurs finalités, vos droits et la manière
              de les exercer, consultez notre{' '}
              <Link to="/privacy" className="text-brass hover:text-brass-dark underline">
                politique de confidentialité
              </Link>.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">6. Propriété intellectuelle</h2>
            <p>
              L'ensemble des éléments présents sur ce site (textes, photographies des pièces,
              dessins, motifs, logo, identité visuelle) est la création de l'éditrice ou de ses
              partenaires, et est protégé par les lois marocaines et internationales relatives à
              la propriété intellectuelle.
            </p>
            <p className="mt-3">
              Toute reproduction, représentation, modification, publication, adaptation totale ou
              partielle des éléments du site est interdite sans autorisation écrite préalable de
              l'éditrice. La reproduction non autorisée engage la responsabilité de son auteur et
              constitue une contrefaçon sanctionnée par les articles 184 et suivants de la loi
              n°2-00 relative aux droits d'auteur et droits voisins.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">7. Crédits photos et illustrations</h2>
            <p>
              Les photographies des pièces sont réalisées par l'atelier sauf mention contraire.
              Certaines illustrations du site proviennent de la plateforme Unsplash et sont
              soumises à la licence Unsplash. Les polices typographiques sont fournies par
              Google Fonts.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">8. Liens externes</h2>
            <p>
              Le site renvoie vers d'autres services tiers (notamment WhatsApp pour la prise de
              rendez-vous et la réservation des pièces). L'éditrice n'exerce aucun contrôle sur
              ces services tiers et décline toute responsabilité quant à leur fonctionnement et
              à leur contenu.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">9. Droit applicable et juridiction</h2>
            <p>
              Les présentes mentions légales sont soumises au droit marocain. En cas de litige,
              et après tentative de résolution amiable, compétence est attribuée aux juridictions
              marocaines du ressort de la résidence de l'éditrice.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">10. Contact</h2>
            <p>
              Pour toute question, réclamation ou demande de retrait de contenu, vous pouvez
              écrire à <strong>fatima@faticaftan.com</strong> ou contacter l'atelier via WhatsApp
              au <strong>+212 7 01 04 86 61</strong>. Une réponse sera apportée sous quelques
              jours ouvrés.
            </p>
          </article>

          <p className="pt-4">
            <Link to="/" className="brass-link editorial-label">
              ← {t('productDetail.back')}
            </Link>
          </p>

        </div>
      </div>
    </section>
  );
}
